import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { AuctionTransportFeeService } from 'src/app/services/auction-transport/auction-transport-fee.service';
import { AuctionTransportFeeModalService } from 'src/app/services/auction-transport-fee-modal/auction-transport-fee-modal.service';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-auction-transport-fee',
  templateUrl: './add-edit-auction-transport-fee.component.html',
  styleUrls: ['./add-edit-auction-transport-fee.component.scss']
})
export class AddEditAuctionTransportFeeComponent extends DestructorComponent implements OnInit {

  @Output() auctionTransportFeeAdded = new EventEmitter<void>();
  @Output() auctionTransportFeeUpdated = new EventEmitter<void>();

  auctionTransportFeeForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedAuctionTransportFee: any = null;

  // Auction Hall search properties
  auctionHallSearchTerm = '';
  auctionHallSearchResults: any[] = [];
  selectedAuctionHall: any = null;
  showAuctionHallDropdown = false;
  isAuctionHallSearching = false;
  private auctionHallSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _auctionTransportFeeService: AuctionTransportFeeService,
    private _auctionTransportFeeModalService: AuctionTransportFeeModalService,
    private _vendorService: VendorService
  ) {
    super();
    this.initializeForm();
    this.setupAuctionHallSearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.auctionTransportFeeForm = this.fb.group({
      auction_hall_id: ['', [Validators.required]],
      auction_hall_name: ['', [Validators.required]],
      auction_fee: [0, [Validators.required, Validators.min(0)]],
      transport_fee: [0, [Validators.required, Validators.min(0)]],
      eaa_transport_fee: [0, [Validators.required, Validators.min(0)]]
    });
  }

  private setupModalListener(): void {
    this.subscriptions.push(
      this._auctionTransportFeeModalService.selectedAuctionTransportFee$.subscribe(auctionTransportFee => {
        this.selectedAuctionTransportFee = auctionTransportFee;
        this.isEditMode = !!auctionTransportFee;

        if (this.isEditMode && auctionTransportFee) {
          this.populateForm(auctionTransportFee);
        } else {
          this.resetForm();
        }
      })
    );
  }

  private populateForm(auctionTransportFee: any): void {
    this.selectedAuctionHall = auctionTransportFee.auction_hall;
    this.auctionHallSearchTerm = auctionTransportFee.auction_hall?.company_name || '';

    this.auctionTransportFeeForm.patchValue({
      auction_hall_id: auctionTransportFee.auction_hall_id || '',
      auction_hall_name: this.auctionHallSearchTerm,
      auction_fee: auctionTransportFee.auction_fee || 0,
      transport_fee: auctionTransportFee.transport_fee || 0,
      eaa_transport_fee: auctionTransportFee.eaa_transport_fee || 0
    });
  }

  private resetForm(): void {
    this.auctionTransportFeeForm.reset();
    this.selectedAuctionHall = null;
    this.auctionHallSearchTerm = '';
    this.auctionHallSearchResults = [];
    this.showAuctionHallDropdown = false;

    this.auctionTransportFeeForm.patchValue({
      auction_hall_id: '',
      auction_hall_name: '',
      auction_fee: 0,
      transport_fee: 0,
      eaa_transport_fee: 0
    });
  }

  // Auction Hall search methods
  private setupAuctionHallSearchDebounce(): void {
    this.auctionHallSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchAuctionHalls(searchTerm);
    });
  }

  private searchAuctionHalls(searchTerm: string): void {
    if (searchTerm.length < 2) {
      this.auctionHallSearchResults = [];
      this.showAuctionHallDropdown = false;
      return;
    }

    this.isAuctionHallSearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { type: 'auction_hall', paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.auctionHallSearchResults = response.data.data || response.data || [];
          } else {
            this.auctionHallSearchResults = [];
          }
          this.isAuctionHallSearching = false;
        },
        error: (error: any) => {
          console.error('Error searching auction halls:', error);
          this.auctionHallSearchResults = [];
          this.isAuctionHallSearching = false;
        }
      })
    );
  }

  onAuctionHallSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.auctionHallSearchTerm = target.value;
    this.selectedAuctionHall = null;

    // Update the form control with the typed value
    this.auctionTransportFeeForm.patchValue({
      auction_hall_name: this.auctionHallSearchTerm,
      auction_hall_id: ''
    });

    if (this.auctionHallSearchTerm.length >= 2) {
      this.auctionHallSearchSubject.next(this.auctionHallSearchTerm);
      this.showAuctionHallDropdown = true;
    } else {
      this.showAuctionHallDropdown = false;
      this.auctionHallSearchResults = [];
    }
  }

  onAuctionHallInputFocus(): void {
    if (this.auctionHallSearchResults.length > 0) {
      this.showAuctionHallDropdown = true;
    }
  }

  onAuctionHallInputBlur(): void {
    setTimeout(() => {
      this.showAuctionHallDropdown = false;
    }, 200);
  }

  selectAuctionHall(auctionHall: any): void {
    this.selectedAuctionHall = auctionHall;
    this.auctionHallSearchTerm = this.getAuctionHallDisplayName(auctionHall);

    this.auctionTransportFeeForm.patchValue({
      auction_hall_name: this.auctionHallSearchTerm,
      auction_hall_id: auctionHall._id || auctionHall.id
    });

    this.showAuctionHallDropdown = false;
  }

  private getAuctionHallDisplayName(auctionHall: any): string {
    return auctionHall.name || auctionHall.company_name || 'Unknown Auction Hall';
  }

  // Form getters for validation
  get auction_hall_name() { return this.auctionTransportFeeForm.get('auction_hall_name'); }
  get auction_fee() { return this.auctionTransportFeeForm.get('auction_fee'); }
  get transport_fee() { return this.auctionTransportFeeForm.get('transport_fee'); }
  get eaa_transport_fee() { return this.auctionTransportFeeForm.get('eaa_transport_fee'); }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_auction_transport_fee_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.auctionTransportFeeForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.auctionTransportFeeForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      // Remove the display name field as it's not needed for API
      delete submitData.auction_hall_name;

      const apiCall = this.isEditMode
        ? this._auctionTransportFeeService.UpdateData(this.selectedAuctionTransportFee._id, submitData)
        : this._auctionTransportFeeService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response) => {
            if (response.status) {
              Swal.fire({
                title: 'Success!',
                text: `Auction transport fee ${this.isEditMode ? 'updated' : 'added'} successfully.`,
                icon: 'success',
                confirmButtonText: 'OK'
              });

              if (this.isEditMode) {
                this.auctionTransportFeeUpdated.emit();
              } else {
                this.auctionTransportFeeAdded.emit();
              }

              this.closeModal();
            } else {
              Swal.fire({
                title: 'Error!',
                text: response.message || `Failed to ${this.isEditMode ? 'update' : 'add'} auction transport fee.`,
                icon: 'error',
                confirmButtonText: 'OK'
              });
            }
            this.isLoading = false;
          },
          error: (error) => {
            console.error(`Error ${this.isEditMode ? 'updating' : 'adding'} auction transport fee:`, error);
            Swal.fire({
              title: 'Error!',
              text: `An error occurred while ${this.isEditMode ? 'updating' : 'adding'} the auction transport fee.`,
              icon: 'error',
              confirmButtonText: 'OK'
            });
            this.isLoading = false;
          }
        })
      );
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.auctionTransportFeeForm.controls).forEach(key => {
        this.auctionTransportFeeForm.get(key)?.markAsTouched();
      });
    }
  }
}
